import { useState, useCallback, useMemo } from 'react';
import apiClient from '../api/api';
import { API_ENDPOINTS } from '../endpoints';
import { toast } from 'react-toastify';
import useOptionConfig from './useOptionConfig';

const useTempUsersPageState = () => {
    const [tempUsers, setTempUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(50);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null)
    const { config } = useOptionConfig();
    const labels = config?.labels?.tempUsersPage || {};
    const fetchTempUsers = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await apiClient.post(API_ENDPOINTS.getTempUsers, {});
            const data = Array.isArray(response.data) ? response.data : [];
            setTempUsers(data);
        } catch (err) {
            console.error('Failed to fetch temp users', err);
            const errMsg = labels.fetchError || 'Failed to fetch temp users';
            setError(errMsg);
            toast.error(errMsg);
        } finally {
            setLoading(false);
        }
    }, [labels.fetchError]);

    const createMasterUser = useCallback(async () => {
        if (!selectedUser) return;

        try {
            const payload = {
                userName: selectedUser.email,
                phoneNo: selectedUser.phoneNo || null,
                firstName: selectedUser.firstName,
                lastName: selectedUser.lastName,
                email: selectedUser.email,
                password: "Welcome",
                otp: selectedUser.otp,
                rank: {
                    code: "STUDENT",
                },
                isActive: "true",
            };

            await apiClient.post(API_ENDPOINTS.createMasterUser, payload);
            toast.success(labels.createSuccess || 'User created successfully!');
            fetchTempUsers();
            setConfirmOpen(false);
            setSelectedUser(null);

        } catch (err) {
            console.error('Error creating user:', err);
            const prefix = labels.createErrorPrefix || 'Failed to create user: ';
            toast.error(`${prefix}${err.response?.data?.message || err.message}`);
        }
    }, [selectedUser, fetchTempUsers, labels.createSuccess, labels.createErrorPrefix]);

    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value);
        setPage(0);
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleCreateClick = (user) => {
        setSelectedUser(user);
        setConfirmOpen(true);
    };

    const handleCloseConfirm = () => {
        setConfirmOpen(false);
        setSelectedUser(null);
    }

    const normalizePhoneNumber = (phone) => {
        return (phone || '').replace(/\D/g, '').trim();
    };

    const filteredUsers = useMemo(() => {
        return tempUsers.filter((user) => {
            if (!searchQuery) return true;
            const query = searchQuery.toLowerCase().trim();
            const fullName = `${user.firstName} ${user.middleName || ''} ${user.lastName}`.toLowerCase().trim();
            const email = (user.email || '').toLowerCase().trim();
            const phoneNo = normalizePhoneNumber(user.phoneNo);

            return (
                fullName.includes(query) ||
                email.includes(query) ||
                phoneNo.includes(query)
            );
        });
    }, [tempUsers, searchQuery]);

    const displayedUsers = useMemo(() => {
        return filteredUsers.slice(
            page * rowsPerPage,
            page * rowsPerPage + rowsPerPage
        );
    }, [filteredUsers, page, rowsPerPage]);

    return {
        tempUsers,
        loading,
        error,
        filteredUsers,
        displayedUsers,
        labels,
        searchQuery,
        page,
        rowsPerPage,
        confirmOpen,
        selectedUser,
        fetchTempUsers,
        createMasterUser,
        handleSearchChange,
        handleChangePage,
        handleChangeRowsPerPage,
        handleCreateClick,
        handleCloseConfirm,
    };
};

export default useTempUsersPageState;
